import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Icon,
  alpha,
  useTheme,
  Tooltip,
  Alert,
  Divider,
  Checkbox,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import moment from "moment";
import { toast } from "react-toastify";
import { accountingService } from "../../../../services/accounting";
import {
  IListVoucherTemplatesRequest,
  IVoucherTemplateListItem,
  IVoucherTemplateDetail,
  ICreateVoucherTemplateRequest,
  IUpdateVoucherTemplateRequest,
  IToggleTemplateStatusRequest,
  IDuplicateVoucherTemplateRequest,
  IDeleteVoucherTemplateRequest,
  IGetVoucherTemplateRequest,
  IVoucherTemplateInput,
  IListCoaRequest,
  IChartOfAccount,
} from "../../accounting.interface";
import { useBranch } from "../../components/BranchContext";
import { AccountSelector } from "../ledger/components/AccountSelector";

export const VoucherTemplatesPage: React.FC = () => {
  const theme = useTheme();
  const { selectedBranch } = useBranch();

  // State
  const [templates, setTemplates] = useState<IVoucherTemplateListItem[]>([]);
  const [postableAccounts, setPostableAccounts] = useState<IChartOfAccount[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<IVoucherTemplateDetail | null>(null);
  const [isCloning, setIsCloning] = useState(false);

  const [formData, setFormData] = useState<Partial<IVoucherTemplateInput>>({
    template_name: "",
    transaction_code: "",
    voucher_type: "payment",
    status: "active",
    debit_account_id: "",
    credit_account_id: "",
    optional_account_per_branch: false,
    allowed_payment_methods: [],
    default_payment_method: "",
    require_narration: true,
    require_attachment: false,
    allow_editing_narration: true,
    allow_split_payments: false,
    narration_template: "",
    enable_reference_linking: false,
    reference_type: "",
    line_templates: [],
  });

  const voucherTypes: Array<"payment" | "receipt" | "journal"> = [
    "payment",
    "receipt",
    "journal",
  ];
  const paymentMethods = ["cash", "bank_transfer", "cheque", "card", "online"];
  const referenceTypes = [
    "Purchase Invoice",
    "Customer Order",
    "Payslip",
    "Expense Report",
    "Asset Purchase",
  ];

  // Fetch posting accounts from COA API
  const fetchPostingAccounts = useCallback(async () => {
    try {
      const requestData: IListCoaRequest = {
        pageIndex: 0,
        pageSize: 1000, // Get all accounts
        sortField: "account_code",
        sortOrder: "asc",
        selected_branch_id:
          selectedBranch === "organization" ? undefined : selectedBranch,
      };

      const response = await accountingService.listCoa(requestData);

      // Convert API accounts to UI format and filter for posting accounts
      const accounts = response.data
        .filter((account) => account.allow_direct_posting)
        .map(
          (account): IChartOfAccount => ({
            id: account.id,
            accountCode: account.account_code,
            accountName: account.account_name,
            accountType: account.account_type,
            currency: account.currency,
            openingBalance: account.opening_balance,
            currentBalance: account.current_balance,
            parentAccountId: account.parent_account_id || undefined,
            isParent: account.is_parent,
            allowDirectPosting: account.allow_direct_posting,
            level: account.level,
            isActive: account.is_active,
            createdAt: new Date(account.created_at).toISOString(),
            updatedAt: new Date(account.updated_at).toISOString(),
          })
        );

      setPostableAccounts(accounts);
    } catch (error: any) {
      console.error("Failed to fetch accounts:", error);
      toast.error("Failed to fetch accounts");
      setPostableAccounts([]);
    }
  }, [selectedBranch]);

  // Fetch voucher templates
  const fetchTemplates = useCallback(async () => {
    setIsLoading(true);
    try {
      const requestData: IListVoucherTemplatesRequest = {
        pageIndex: 0,
        pageSize: 1000, // Get all templates
        sortField: "created_at",
        sortOrder: "desc",
        selected_branch_id:
          selectedBranch === "organization" ? undefined : selectedBranch,
      };

      const response = await accountingService.listVoucherTemplates(
        requestData
      );
      setTemplates(response.data);
    } catch (error: any) {
      console.error("Failed to fetch templates:", error);
      toast.error("Failed to fetch voucher templates");
      setTemplates([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedBranch]);

  // Fetch template details for editing
  const fetchTemplateDetails = useCallback(async (templateId: string) => {
    try {
      const requestData: IGetVoucherTemplateRequest = {
        template_id: templateId,
      };
      const response = await accountingService.getVoucherTemplate(requestData);
      return response.data;
    } catch (error: any) {
      console.error("Failed to fetch template details:", error);
      toast.error("Failed to fetch template details");
      return null;
    }
  }, []);

  // Effects
  useEffect(() => {
    fetchPostingAccounts();
    fetchTemplates();
  }, [fetchPostingAccounts, fetchTemplates]);

  const handleOpenDialog = async (
    template?: IVoucherTemplateListItem,
    clone = false
  ) => {
    if (template) {
      const templateDetails = await fetchTemplateDetails(template.template_id);
      if (templateDetails) {
        setEditingTemplate(clone ? null : templateDetails);
        setIsCloning(clone);
        setFormData({
          template_name: clone
            ? `${templateDetails.template_name} (Copy)`
            : templateDetails.template_name,
          transaction_code: clone
            ? `${templateDetails.transaction_code}_COPY`
            : templateDetails.transaction_code,
          voucher_type: templateDetails.voucher_type,
          status: templateDetails.status,
          debit_account_id: templateDetails.debit_account_id,
          credit_account_id: templateDetails.credit_account_id,
          optional_account_per_branch:
            templateDetails.optional_account_per_branch,
          allowed_payment_methods:
            templateDetails.allowed_payment_methods || [],
          default_payment_method: templateDetails.default_payment_method,
          require_narration: templateDetails.require_narration,
          require_attachment: templateDetails.require_attachment,
          allow_editing_narration: templateDetails.allow_editing_narration,
          allow_split_payments: templateDetails.allow_split_payments,
          narration_template: templateDetails.narration_template,
          enable_reference_linking: templateDetails.enable_reference_linking,
          reference_type: templateDetails.reference_type,
          line_templates: templateDetails.line_templates || [],
        });
      }
    } else {
      setEditingTemplate(null);
      setIsCloning(false);
      setFormData({
        template_name: "",
        transaction_code: "",
        voucher_type: "payment",
        status: "active",
        debit_account_id: "",
        credit_account_id: "",
        optional_account_per_branch: false,
        allowed_payment_methods: [],
        default_payment_method: "",
        require_narration: true,
        require_attachment: false,
        allow_editing_narration: true,
        allow_split_payments: false,
        narration_template: "",
        enable_reference_linking: false,
        reference_type: "",
        line_templates: [],
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingTemplate(null);
    setIsCloning(false);
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      if (editingTemplate && !isCloning) {
        // Update existing template
        const updateData: IUpdateVoucherTemplateRequest = {
          template_id: editingTemplate.template_id,
          template: formData,
        };
        await accountingService.updateVoucherTemplate(updateData);
        toast.success("Template updated successfully");
      } else if (isCloning && editingTemplate) {
        // Duplicate template
        const duplicateData: IDuplicateVoucherTemplateRequest = {
          source_template_id: editingTemplate.template_id,
          new_template_name: formData.template_name!,
        };
        await accountingService.duplicateVoucherTemplate(duplicateData);
        toast.success("Template duplicated successfully");
      } else {
        // Create new template
        const createData: ICreateVoucherTemplateRequest = {
          template: formData as IVoucherTemplateInput,
        };
        await accountingService.createVoucherTemplate(createData);
        toast.success("Template created successfully");
      }

      await fetchTemplates();
      handleCloseDialog();
    } catch (error: any) {
      console.error("Failed to save template:", error);
      toast.error(error.message || "Failed to save template");
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleStatus = async (
    templateId: string,
    currentStatus: "active" | "inactive"
  ) => {
    setIsLoading(true);
    try {
      const newStatus: "active" | "inactive" =
        currentStatus === "active" ? "inactive" : "active";
      const requestData: IToggleTemplateStatusRequest = {
        template_id: templateId,
        new_status: newStatus,
      };

      await accountingService.toggleTemplateStatus(requestData);
      toast.success(
        `Template ${
          newStatus === "active" ? "activated" : "deactivated"
        } successfully`
      );
      await fetchTemplates();
    } catch (error: any) {
      console.error("Failed to toggle template status:", error);
      toast.error("Failed to update template status");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this template? This action cannot be undone."
      )
    ) {
      return;
    }

    setIsLoading(true);
    try {
      const deleteData: IDeleteVoucherTemplateRequest = {
        template_id: templateId,
        deletion_reason: "User requested deletion",
      };

      await accountingService.deleteVoucherTemplate(deleteData);
      toast.success("Template deleted successfully");
      await fetchTemplates();
    } catch (error: any) {
      console.error("Failed to delete template:", error);
      toast.error("Failed to delete template");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFieldChange = (
    field: keyof IVoucherTemplateInput,
    value: any
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePaymentMethodToggle = (method: string) => {
    const currentMethods = formData.allowed_payment_methods || [];
    if (currentMethods.includes(method)) {
      setFormData((prev) => ({
        ...prev,
        allowed_payment_methods: currentMethods.filter((m) => m !== method),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        allowed_payment_methods: [...currentMethods, method],
      }));
    }
  };

  const getAccountName = (accountId: string) => {
    const account = postableAccounts.find((acc) => acc.id === accountId);
    return account
      ? `${account.accountCode} - ${account.accountName}`
      : "Unknown Account";
  };

  const generatePreview = (template: string) => {
    // Simple template preview logic
    const today = moment().format("YYYY-MM-DD");
    return template
      .replace("{{date}}", today)
      .replace("{{voucher_type}}", formData.voucher_type || "payment")
      .replace("{{reference}}", "REF-123");
  };

  const isFormValid = () => {
    return !!(
      formData.template_name &&
      formData.transaction_code &&
      formData.voucher_type &&
      formData.debit_account_id &&
      formData.credit_account_id
    );
  };

  const refreshData = useCallback(async () => {
    await Promise.all([fetchTemplates(), fetchPostingAccounts()]);
  }, [fetchTemplates, fetchPostingAccounts]);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          p: 3,
          bgcolor: "background.paper",
          borderRadius: 0.5,
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.04)}`,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
          >
            Voucher Templates
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", fontSize: "1rem" }}
          >
            Create and manage reusable voucher templates for standardized
            transactions
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<Icon>add</Icon>}
          onClick={() => handleOpenDialog()}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            py: 1.5,
          }}
        >
          Create New Template
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3, px: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: "primary.main" }}
              >
                {templates.length}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Total Templates
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: "success.main" }}
              >
                {templates.filter((t) => t.status === "active").length}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Active Templates
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: "warning.main" }}
              >
                {templates.filter((t) => t.status === "inactive").length}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Inactive Templates
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: "info.main" }}
              >
                {new Set(templates.map((t) => t.voucher_type)).size}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Voucher Types
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Templates Table */}
      <Box sx={{ flex: 1, px: 3 }}>
        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow
                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>
                      Template Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Transaction Code
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>COA Mapping</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {templates.map((template) => (
                    <TableRow key={template.template_id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {template.template_name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary" }}
                          >
                            Created:{" "}
                            {moment(template.created_at).format("MMM DD, YYYY")}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                        >
                          {template.transaction_code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={
                            template.voucher_type.charAt(0).toUpperCase() +
                            template.voucher_type.slice(1)
                          }
                          color={
                            template.status === "active" ? "success" : "error"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ color: "success.main", display: "block" }}
                          >
                            Dr:{" "}
                            {template.coa_mapping_summary.debit_account
                              ? `${template.coa_mapping_summary.debit_account.account_code} - ${template.coa_mapping_summary.debit_account.account_name}`
                              : "Not set"}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "error.main", display: "block" }}
                          >
                            Cr:{" "}
                            {template.coa_mapping_summary.credit_account
                              ? `${template.coa_mapping_summary.credit_account.account_code} - ${template.coa_mapping_summary.credit_account.account_name}`
                              : "Not set"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={
                            template.status === "active" ? "Active" : "Inactive"
                          }
                          color={
                            template.status === "active" ? "success" : "error"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title="Edit template">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(template)}
                              sx={{ color: "primary.main" }}
                            >
                              <Icon>edit</Icon>
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Clone template">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(template, true)}
                              sx={{ color: "info.main" }}
                            >
                              <Icon>content_copy</Icon>
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              template.status === "active"
                                ? "Deactivate"
                                : "Activate"
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleToggleStatus(
                                  template.template_id,
                                  template.status
                                )
                              }
                              sx={{
                                color:
                                  template.status === "active"
                                    ? "warning.main"
                                    : "success.main",
                              }}
                            >
                              <Icon>
                                {template.status === "active"
                                  ? "pause"
                                  : "play_arrow"}
                              </Icon>
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete template">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDeleteTemplate(template.template_id)
                              }
                              sx={{ color: "error.main" }}
                            >
                              <Icon>delete</Icon>
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      {/* Template Form Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            minHeight: "80vh",
          },
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon sx={{ color: "primary.main" }}>description</Icon>
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {editingTemplate && !isCloning
                  ? "Edit Voucher Template"
                  : isCloning
                  ? "Clone Voucher Template"
                  : "Create New Voucher Template"}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {editingTemplate && !isCloning
                  ? `Editing: ${editingTemplate.template_name}`
                  : isCloning
                  ? `Cloning: ${editingTemplate?.template_name}`
                  : "Configure a new reusable voucher template"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={4}>
            {/* Section 1: General Information */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                1. General Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Template Name"
                    value={formData.template_name || ""}
                    onChange={(e) =>
                      handleFieldChange("template_name", e.target.value)
                    }
                    placeholder="e.g., Vendor Payment - Bank"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Transaction Code"
                    value={formData.transaction_code || ""}
                    onChange={(e) =>
                      handleFieldChange("transaction_code", e.target.value)
                    }
                    placeholder="e.g., VENDOR_PAYMENT_BANK"
                    required
                    helperText="Unique internal reference"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Voucher Type</InputLabel>
                    <Select
                      value={formData.voucher_type || "payment"}
                      label="Voucher Type"
                      onChange={(e) =>
                        handleFieldChange("voucher_type", e.target.value)
                      }
                    >
                      {voucherTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status || "active"}
                      label="Status"
                      onChange={(e) =>
                        handleFieldChange("status", e.target.value)
                      }
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Section 2: COA Mapping */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                2. COA Mapping
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, color: "success.main" }}
                  >
                    Debit Account *
                  </Typography>
                  <AccountSelector
                    selectedAccountId={formData.debit_account_id || ""}
                    onAccountChange={(accountId) =>
                      handleFieldChange("debit_account_id", accountId)
                    }
                    postingAccounts={postableAccounts}
                    selectedAccount={
                      postableAccounts.find(
                        (acc) => acc.id === formData.debit_account_id
                      ) || null
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, color: "error.main" }}
                  >
                    Credit Account *
                  </Typography>
                  <AccountSelector
                    selectedAccountId={formData.credit_account_id || ""}
                    onAccountChange={(accountId) =>
                      handleFieldChange("credit_account_id", accountId)
                    }
                    postingAccounts={postableAccounts}
                    selectedAccount={
                      postableAccounts.find(
                        (acc) => acc.id === formData.credit_account_id
                      ) || null
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.optional_account_per_branch || false}
                        onChange={(e) =>
                          handleFieldChange(
                            "optional_account_per_branch",
                            e.target.checked
                          )
                        }
                      />
                    }
                    label="Optional Account Per Branch (Advanced)"
                  />
                  <FormHelperText>
                    If enabled, allow template to vary mapping by branch
                  </FormHelperText>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Section 3: Payment Methods (if applicable) */}
            {(formData.voucher_type === "payment" ||
              formData.voucher_type === "receipt") && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    3. Allowed Payment Methods
                  </Typography>
                  <Grid container spacing={2}>
                    {paymentMethods.map((method) => (
                      <Grid item xs={12} sm={6} md={4} key={method}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={(
                                formData.allowed_payment_methods || []
                              ).includes(method)}
                              onChange={() => handlePaymentMethodToggle(method)}
                            />
                          }
                          label={method}
                        />
                      </Grid>
                    ))}
                  </Grid>
                  {(formData.allowed_payment_methods || []).length > 0 && (
                    <FormControl fullWidth sx={{ mt: 2 }}>
                      <InputLabel>Default Payment Method</InputLabel>
                      <Select
                        value={formData.default_payment_method || ""}
                        label="Default Payment Method"
                        onChange={(e) =>
                          handleFieldChange(
                            "default_payment_method",
                            e.target.value
                          )
                        }
                      >
                        {(formData.allowed_payment_methods || []).map(
                          (method) => (
                            <MenuItem key={method} value={method}>
                              {method}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>
              </>
            )}

            {/* Section 4: Default Behavior Options */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                4. Default Behavior Options
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.require_narration || false}
                        onChange={(e) =>
                          handleFieldChange(
                            "require_narration",
                            e.target.checked
                          )
                        }
                      />
                    }
                    label="Require Narration"
                  />
                  <FormHelperText>
                    Prevent voucher submission without narration
                  </FormHelperText>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.require_attachment || false}
                        onChange={(e) =>
                          handleFieldChange(
                            "require_attachment",
                            e.target.checked
                          )
                        }
                      />
                    }
                    label="Require Attachment"
                  />
                  <FormHelperText>Require file upload for audit</FormHelperText>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.allow_editing_narration || false}
                        onChange={(e) =>
                          handleFieldChange(
                            "allow_editing_narration",
                            e.target.checked
                          )
                        }
                      />
                    }
                    label="Allow Editing of Narration"
                  />
                  <FormHelperText>
                    If off, narration is locked to template
                  </FormHelperText>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.allow_split_payments || false}
                        onChange={(e) =>
                          handleFieldChange(
                            "allow_split_payments",
                            e.target.checked
                          )
                        }
                      />
                    }
                    label="Allow Split Payments"
                  />
                  <FormHelperText>
                    Allow splitting amount by multiple payment methods
                  </FormHelperText>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Section 5: Narration Template */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                5. Narration Template with Placeholders
              </Typography>
              <TextField
                fullWidth
                label="Narration Template"
                value={formData.narration_template || ""}
                onChange={(e) =>
                  handleFieldChange("narration_template", e.target.value)
                }
                placeholder="e.g., Payment to {{vendor}} for invoice {{invoice_no}}"
                multiline
                rows={3}
                required
                helperText="Use {{variable_name}} for dynamic placeholders"
              />
              {formData.narration_template && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Preview After Placeholder Fill:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {generatePreview(formData.narration_template)}
                  </Typography>
                </Alert>
              )}
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Section 6: Reference Link Settings */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                6. Reference Link Settings
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enable_reference_linking || false}
                        onChange={(e) =>
                          handleFieldChange(
                            "enable_reference_linking",
                            e.target.checked
                          )
                        }
                      />
                    }
                    label="Enable Reference Linking"
                  />
                  <FormHelperText>
                    Allows linking this voucher to another entity (Invoice, PO,
                    etc.)
                  </FormHelperText>
                </Grid>
                {formData.enable_reference_linking && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Reference Type</InputLabel>
                      <Select
                        value={formData.reference_type || ""}
                        label="Reference Type"
                        onChange={(e) =>
                          handleFieldChange("reference_type", e.target.value)
                        }
                      >
                        {referenceTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleCloseDialog} size="large">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!isFormValid()}
            size="large"
            startIcon={<Icon>save</Icon>}
          >
            {editingTemplate && !isCloning
              ? "Update Template"
              : "Create Template"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
